import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { api, ICON_URL, auth } from "@/lib/api";
import ServerSelector from "@/components/dashboard/ServerSelector";
import { FEATURE_GROUPS } from "@/lib/features-config";
import { ChevronDown, Crown, LogOut } from "lucide-react";

export default function Sidebar() {
  const navigate = useNavigate();
  const user = auth.getUser();
  const [isAdmin, setIsAdmin] = useState(false);
  const [open, setOpen] = useState(() => {
    const init = {};
    FEATURE_GROUPS.forEach((g) => { if (g.label) init[g.label] = true; });
    return init;
  });

  useEffect(() => {
    api.get("/admin/check").then((r) => setIsAdmin(!!r.data.is_admin)).catch(() => setIsAdmin(false));
  }, []);

  const logout = () => {
    auth.clear();
    localStorage.removeItem("606_active_guild");
    navigate("/");
  };

  return (
    <aside data-testid="dashboard-sidebar"
      className="w-72 shrink-0 bg-brand-surface border-r border-brand-line min-h-screen flex flex-col">
      <a href="/" className="flex items-center gap-3 px-5 h-16 border-b border-brand-line shrink-0" data-testid="sidebar-home-link">
        <img src={ICON_URL} alt="606" className="h-9 w-9 object-contain" />
        <div>
          <div className="font-heading text-sm font-bold uppercase tracking-tight leading-none">606 COMMAND</div>
          <div className="font-mono-tabular text-[10px] text-brand-orange tracking-widest">CONTROL CENTER</div>
        </div>
      </a>

      <ServerSelector />

      {isAdmin && (
        <div className="px-3 pt-3" data-testid="sidebar-admin-section">
          <NavLink
            to="/dashboard/admin"
            data-testid="nav-admin"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2.5 text-sm rounded-lg transition-colors border ${
                isActive
                  ? "bg-brand-orange text-black font-bold border-brand-orange"
                  : "text-brand-orange border-brand-orange/40 hover:border-brand-orange hover:bg-brand-orange/10"
              }`
            }
          >
            <Crown className="w-4 h-4 shrink-0" />
            <span className="font-heading uppercase tracking-wider truncate flex-1 text-left">Admin Panel</span>
            <span className="font-mono-tabular text-[8px] px-1.5 py-0.5 bg-brand-orange/20 rounded uppercase">SUPER</span>
          </NavLink>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto tactical-scroll px-3 py-3 space-y-4">
        {FEATURE_GROUPS.map((group, gi) => (
          <div key={gi}>
            {group.label ? (
              <button
                onClick={() => setOpen((o) => ({ ...o, [group.label]: !o[group.label] }))}
                className="w-full flex items-center justify-between px-2 mb-1 group"
                data-testid={`group-toggle-${group.label}`}
              >
                <span className="overline">{group.label}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 transition-transform ${open[group.label] ? "" : "-rotate-90"}`} />
              </button>
            ) : null}
            {(group.label === null || open[group.label]) && (
              <div className="space-y-0.5">
                {group.items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <NavLink
                      key={it.id}
                      to={it.to}
                      end={it.id === "overview"}
                      data-testid={`nav-${it.id}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors group ${
                          isActive ? "bg-brand-orange text-black font-bold" : "text-zinc-400 hover:text-white hover:bg-brand-elevated"
                        }`
                      }
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="font-heading uppercase tracking-wider truncate flex-1 text-left">{it.label}</span>
                      {it.status === "soon" && (
                        <span className="font-mono-tabular text-[8px] px-1.5 py-0.5 border border-zinc-700 text-zinc-500 rounded uppercase">SOON</span>
                      )}
                      {it.badge && (
                        <span className="font-mono-tabular text-[8px] px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded uppercase">{it.badge}</span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </nav>

      <div className="p-4 border-t border-brand-line shrink-0">
        <div className="flex items-center gap-3 mb-3 px-1">
          {user?.avatar ? (
            <img src={user.avatar} alt={user.username} className="w-9 h-9 rounded-md object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-md bg-brand-orange/20 border border-brand-orange flex items-center justify-center font-bold text-brand-orange uppercase">
              {user?.username?.[0] || "U"}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="sidebar-username">{user?.username || "Guest"}</div>
            <div className="font-mono-tabular text-[10px] text-zinc-500 uppercase">{user?.is_demo ? "DEMO MODE" : "DISCORD AUTH"}</div>
          </div>
        </div>
        <button onClick={logout} className="w-full btn-ghost flex items-center justify-center gap-2 border border-brand-line hover:border-brand-orange" data-testid="sidebar-logout-btn">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </aside>
  );
}
